import { QueryClient } from "@tanstack/react-query";
import {
	PersistedClient,
	Persister,
	PersistQueryClientProvider,
} from "@tanstack/react-query-persist-client";
import { del, get, set } from "idb-keyval";

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			networkMode: "always",
			staleTime: 5_000,
			cacheTime: Infinity,
			retry: false,
		},
		mutations: {
			networkMode: "always",
			cacheTime: 0,
			retry: false,
		},
	},
});

// Throttled IDB persister - only writes max once every 2 seconds
function createIDBPersister(
	idbValidKey: IDBValidKey = "served:staff:query-client",
) {
	let writeTimer: NodeJS.Timeout | null = null;
	let pendingClient: PersistedClient | null = null;
	let isWriting = false;

	const flushWrite = async () => {
		if (!pendingClient || isWriting) return;

		isWriting = true;
		const clientToWrite = pendingClient;
		pendingClient = null;

		try {
			performance.mark("idb-write-start");
			await set(idbValidKey, clientToWrite);
			performance.mark("idb-write-end");
			performance.measure("idb-write", "idb-write-start", "idb-write-end");
		} finally {
			isWriting = false;
		}
	};

	// Ensure writes happen before tab close
	if (typeof window !== "undefined") {
		window.addEventListener("beforeunload", () => {
			if (pendingClient) {
				// Force immediate IDB write before unload (blocks main thread but necessary)
				// Using void to fire-and-forget since we can't await in beforeunload
				void set(idbValidKey, pendingClient);
			}
		});

		// Also flush on visibility change (when switching tabs)
		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "hidden" && pendingClient) {
				void flushWrite();
			}
		});
	}

	return {
		persistClient: (client: PersistedClient) => {
			pendingClient = client;

			// Throttle: only write max once every 2 seconds
			if (writeTimer) {
				clearTimeout(writeTimer);
			}

			writeTimer = setTimeout(() => {
				void flushWrite();
			}, 2000); // Adjust this value based on your needs
		},
		restoreClient: async () => {
			return await get<PersistedClient>(idbValidKey);
		},
		removeClient: async () => {
			await del(idbValidKey);
		},
	} satisfies Persister;
}

export const QueryProvider = ({ children }) => {
	return (
		<PersistQueryClientProvider
			client={queryClient}
			persistOptions={{
				persister: createIDBPersister(),
				maxAge: Infinity,
			}}>
			{children}
		</PersistQueryClientProvider>
	);
};
